import { TipType } from '@/api/gql/graphql';
import {
  Button,
  FileUpload,
  SelectDropDown,
  TextField,
} from '@/components/Common';
import { TextfieldType } from '@/components/Common/TextField/TextField.types';
import { Modal } from '@/components/Layout';
import { AlertModal } from '@/components/Layout/AlertModal/AlertModal';
import { ModalPositions } from '@/components/Layout/Modal/Modal.types';
import { TextEditor } from '@/components/Ui';
import { useEffect, useId } from 'react';
import { Controller, FieldError } from 'react-hook-form';
import { useTipsList } from './TipsList.hook';

const tipTypeOptions = Object.keys(TipType).map((key) => ({
  _id: key.toLowerCase(),
  name: key,
}));

export const TipsForm = () => {
  const id = useId();

  const {
    isOpenForm,
    tipsCategories = [],
    errorsTip,
    registerTip,
    setValueTip,
    control,
    handleDraftTip,
    handleCreateAndPublishTip,
    isCreatePublishModalOpen,
    setIsCreatePublishModalOpen,
    getModalTitle,
    getModalMessage,
    getPublishButtonText,
    handleConfirmCreateAndPublish,
    isCreatingTip,
    isEditMode,
    handleDraftUpdateTip,
    handleCloseForm,
    tip,
    isUpdatingTip,
    generateUrlLoading,
    tipType,
    handleTipTypeChange,
    setTipType,
  } = useTipsList();

  useEffect(() => {
    if (isEditMode && tip) {
      const initFields = (
        fieldsObj: Record<string, string | boolean | File | undefined>,
      ) => {
        Object.entries(fieldsObj).forEach(([field, value]) =>
          setValueTip(field, value),
        );
      };

      initFields({
        title: tip.title,
        category: tip.category?._id,
        type: tip.type,
        description: tip.description,
        text: tip.content?.html || '',
      });

      handleTipTypeChange({
        _id: tip.type,
        name: tip.type,
      });
      setTipType(tip.type);
    }
  }, [isEditMode, tip, setValueTip]);

  return (
    <Modal
      isOpen={isOpenForm}
      onClose={handleCloseForm}
      position={ModalPositions.RIGHT}
      modalStyle="p-0"
      headerStyle="bg-black"
    >
      <div className="h-[90vh] w-[70vw] bg-black relative">
        <form className={`grid grid-cols-[33%_auto] items-start`}>
          <div className="flex flex-col gap-4 h-[85vh] overflow-auto py-4 px-8">
            <h3 className="text-[30px] font-raleway font-bold leading-[49.32px] text-white text-center">
              {isEditMode ? 'Modifier un conseil' : 'Ajouter un conseil'}
            </h3>
            <ul className="flex flex-col gap-8 pt-4 relative h-full">
              <li className="grid grid-cols-[15%_auto] gap-4">
                <div className="flex items-center justify-center h-[48px] w-[48px] rounded-full bg-[#1D1D1D]">
                  <i className="pi pi-graduation-cap text-[#939393] text-xl"></i>
                </div>
                <SelectDropDown
                  selectDropDownStyle={{
                    borderRadius: '0.375rem',
                    color: '#fff',
                    padding: ' 0.5rem',
                  }}
                  placeholder="Type de conseil"
                  name="type"
                  setValue={setValueTip}
                  initialSelectedOptions={
                    isEditMode && tipType
                      ? {
                          _id: tipType.toLowerCase(),
                          name: tipType,
                        }
                      : null
                  }
                  errorStyle="text-red-500"
                  helpText={(errorsTip?.type as FieldError)?.message}
                  options={tipTypeOptions}
                  handleChange={handleTipTypeChange}
                />
              </li>
              <li className="grid grid-cols-[15%_auto] gap-4">
                <div className="flex items-center justify-center h-[48px] w-[48px] rounded-full bg-[#1D1D1D]">
                  <i className="pi pi-file text-[#939393] text-xl"></i>
                </div>
                <TextField
                  inputStyle={{ background: '#1D1D1D' }}
                  placeholder={'Intitulé du conseil'}
                  name="title"
                  register={registerTip('title')}
                  textFieldType={TextfieldType.INPUT}
                  helpText={(errorsTip?.title as FieldError)?.message}
                  errorStyle="text-red-500"
                />
              </li>
              <li className="grid grid-cols-[15%_auto] gap-4">
                <div className="flex items-center justify-center h-[48px] w-[48px] rounded-full bg-[#1D1D1D]">
                  <i className="pi pi-heart text-[#939393] text-xl"></i>
                </div>
                <SelectDropDown
                  selectDropDownStyle={{
                    borderRadius: '0.375rem',
                    color: '#fff',
                    padding: ' 0.5rem',
                  }}
                  placeholder="Catégorie"
                  name="category"
                  setValue={setValueTip}
                  initialSelectedOptions={
                    isEditMode
                      ? tip?.category
                        ? {
                            _id: tip.category._id,
                            name: tip.category.name,
                          }
                        : undefined
                      : null
                  }
                  errorStyle="text-red-500"
                  helpText={(errorsTip?.category as FieldError)?.message}
                  options={tipsCategories || []}
                  handleChange={() => {}}
                />
              </li>
              <li className="grid grid-cols-[15%_auto] gap-4">
                <div className="flex items-center justify-center h-[48px] w-[48px] rounded-full bg-[#1D1D1D]">
                  <i className="pi pi-headphones text-[#939393] text-xl"></i>
                </div>
                <FileUpload
                  id={`${id}-audio`}
                  placeholder="Choisir un fichier"
                  name="audioFile"
                  disabled={tipType?.toUpperCase() !== TipType.Audio}
                  setValue={setValueTip}
                  initialSelectedFile={
                    isEditMode ? tip?.audio || undefined : ''
                  }
                  helpText={(errorsTip?.audioFile as FieldError)?.message}
                  errorStyle="text-red-500"
                  allowedFileTypes={['audio/mp3', 'audio/wav', 'audio/mpeg']}
                />
              </li>
              <div className="absolute bottom-8 left-0 right-0 flex flex-col gap-2 mb-2">
                <Button
                  onClick={isEditMode ? handleDraftUpdateTip : handleDraftTip}
                  title={
                    isUpdatingTip || generateUrlLoading
                      ? 'Enregistrer brouillon ...'
                      : 'Enregistrer brouillon'
                  }
                  className="bg-white text-black"
                  type="button"
                />
                <Button
                  onClick={handleCreateAndPublishTip}
                  title={getPublishButtonText()}
                  type="button"
                />
              </div>
            </ul>
          </div>
          <div className="flex flex-col gap-6 p-4 h-[85vh] overflow-auto py-4 px-8">
            {tipType?.toUpperCase() === TipType.Text ? (
              <Controller
                name="text"
                control={control}
                render={({ field }) => (
                  <TextEditor
                    backgroundColor="#000"
                    onChange={field.onChange}
                    initialValue={isEditMode ? tip?.content?.html || '' : ''}
                  />
                )}
              />
            ) : (
              <div className="text-white flex items-center justify-center h-full">
                Audio
              </div>
            )}
          </div>
        </form>
        <AlertModal
          isOpen={isCreatePublishModalOpen}
          onClose={() => setIsCreatePublishModalOpen(false)}
          title={getModalTitle()}
          message={getModalMessage()}
          buttonTitle={
            isCreatingTip || generateUrlLoading
              ? 'En cours...'
              : getPublishButtonText()
          }
          buttonCancelTitle="Annuler"
          handleConfirm={handleConfirmCreateAndPublish}
        />
      </div>
    </Modal>
  );
};

export default TipsForm;
